import { CONSTRUCTION_CREWS } from './constants';

/** All money in the simulation is an integer number of cents. */
export const dollars = (amount: number): number => amount * 100;

/** Wire and save ids for building types. Append only; never renumber. */
export const BuildingType = {
  Tower: 1,
  Terminal: 2,
  Runway: 3,
  Stand: 4,
  Parking: 5,
  FuelDepot: 6,
  FireStation: 7,
  Hangar: 8,
  GroundHandling: 9,
  /** The sign the state incentive grant requires (see grant.ts). */
  GrantSign: 10,
  /** Taxi rank and bus station next to a terminal. */
  TransitHub: 11,
  /** Warehouse and apron for air freight. */
  CargoTerminal: 12,
  /** Airport hotel: rooms for passengers and airline crews. */
  Hotel: 13,
  /** Railway station by the terminal: passengers arrive by train. */
  RailStation: 14,
  /** Construction office: more building crews at once. */
  ConstructionOffice: 15,
  /** Flight kitchen: meals for the airlines, faster catering. */
  CateringKitchen: 16,
  /** Wildlife control: falconers and bird scarers keep birds off the runways. */
  WildlifeControl: 17,
  /** The military zone: checkpoint at its gate. Needed for any contract with the ministry. */
  MilitaryGate: 18,
  /** Hardened aircraft shelter: where a fighter parks (a stand of its own kind). */
  Shelter: 19,
  /** Military apron: where airlifters and drones park. */
  MilitaryApron: 20,
  /** Squadron operations: needed to base fighters. */
  OpsCenter: 21,
  /** Munitions store: needed to base fighters (arming). */
  Munitions: 22,
  /** Air-defence site: search radar and missile launchers; the ministry pays more for each sortie. */
  AirDefence: 23,
  /** De-icing pad: aircraft are de-iced on their way to the runway instead of at their stand. */
  DeicePad: 24,
  /** Remote parking position: aircraft wait here between flights (night stops, rentals). */
  RemoteParking: 25,
  /** Contact stand: set against a terminal, passengers board by jet bridge. */
  ContactStand: 26,
  /** Helipad: private helicopters land and take off on it; their passengers come by car. */
  Helipad: 27,
  /** Spotters' deck: a viewing terrace by a runway; visitors pay to watch the departures. */
  SpotterDeck: 28,
  /** A short, narrow strip for small single-engine aircraft (see Size). */
  SmallRunway: 29,
  /** A long, wide runway for the biggest aircraft: jumbos and giant freighters (see Size). */
  LargeRunway: 30,
  /** A small stand for a single-engine aircraft. */
  SmallStand: 31,
  /** A large stand for the biggest aircraft (and any other, unless kept for them). */
  LargeStand: 32,
  /** A small remote parking position. */
  SmallParking: 33,
  /** A large remote parking position. */
  LargeParking: 34,
  /** Military control tower: the higher it is, the more the ministry offers. */
  MilitaryTower: 35,
  /**
   * The runway, grown by levels: a short strip for the small aircraft, longer and wider for
   * bigger ones, then lights, ILS and at last the widest runway for the biggest of all (see
   * runwayGrade, runwayLights, runwaySize). It replaces the three kinds of runway before it,
   * which go on working where they were built.
   */
  MainRunway: 36,
  /**
   * Large de-icing pad: room for the biggest aircraft. A widebody, a jumbo or a giant freighter
   * does not fit the (medium) de-icing pad; without this one they are sprayed at their stand.
   */
  LargeDeicePad: 37,
  /**
   * The airport directorate: the management's office block. Opens the report book (how the
   * airport has done, all time); its staff drive in over a service road.
   */
  Directorate: 38,
  /** The airport city (see commerce.ts): a café on the landside. */
  Cafe: 39,
  /** A restaurant. */
  Restaurant: 40,
  /** A shopping mall: shops, a food court and a car park. */
  Mall: 41,
  /** A fuel station by the road: cars on their way to the terminals fill up. */
  FuelStation: 42,
  /** An office block: its tenants pay rent, its workers eat and drink nearby. */
  OfficePark: 43,
  /** A park: sells nothing, makes the businesses around it more pleasant to visit. */
  CityPark: 44,
} as const;
export type BuildingTypeId = (typeof BuildingType)[keyof typeof BuildingType];

export interface BuildingLevelDef {
  /** Cents. */
  readonly cost: number;
  /** Construction time in game hours. */
  readonly buildHours: number;
  /** Completed control tower level needed to build or upgrade to this level. */
  readonly requiresTower: number;
}

export interface BuildingDef {
  readonly type: BuildingTypeId;
  /** Stable identifier used for i18n and models. */
  readonly key: string;
  /** Footprint in tiles when not rotated. */
  readonly width: number;
  readonly height: number;
  readonly levels: readonly BuildingLevelDef[];
  /** Maximum number of this building, indexed by completed tower level (0–5). */
  readonly limits: readonly number[];
}

const lv = (cost: number, buildHours: number, requiresTower: number): BuildingLevelDef => ({
  cost: dollars(cost),
  buildHours,
  requiresTower,
});

/**
 * Building catalogue. Balance numbers live here only; the GDD describes the intent.
 * Changing any number changes simulation behaviour: bump SIM_VERSION.
 */
export const BUILDINGS: Readonly<Record<BuildingTypeId, BuildingDef>> = {
  [BuildingType.Tower]: {
    type: BuildingType.Tower,
    key: 'tower',
    width: 4,
    height: 4,
    levels: [
      lv(20_000, 1, 0),
      lv(60_000, 4, 0),
      lv(150_000, 8, 0),
      lv(400_000, 16, 0),
      lv(1_000_000, 24, 0),
    ],
    limits: [1, 1, 1, 1, 1, 1],
  },
  [BuildingType.Terminal]: {
    type: BuildingType.Terminal,
    key: 'terminal',
    width: 16,
    height: 10,
    levels: [
      lv(40_000, 2, 1),
      lv(90_000, 6, 2),
      lv(220_000, 12, 3),
      lv(500_000, 18, 4),
      lv(1_200_000, 24, 5),
    ],
    limits: [0, 1, 1, 2, 2, 3],
  },
  [BuildingType.Runway]: {
    type: BuildingType.Runway,
    key: 'runway',
    width: 64,
    height: 4,
    levels: [lv(60_000, 3, 1), lv(120_000, 8, 2), lv(300_000, 16, 4)],
    // Four from tower level 4 (a big hub's runway count).
    limits: [0, 1, 1, 2, 4, 4],
  },
  [BuildingType.Stand]: {
    type: BuildingType.Stand,
    key: 'stand',
    width: 10,
    height: 10,
    levels: [lv(15_000, 1, 1), lv(45_000, 4, 3)],
    limits: [0, 2, 4, 6, 9, 12],
  },
  // The same length as the others (the sim's runway geometry is per tile along it), narrower.
  [BuildingType.SmallRunway]: {
    type: BuildingType.SmallRunway,
    key: 'smallRunway',
    width: 64,
    height: 3,
    levels: [lv(25_000, 2, 1)],
    limits: [0, 1, 1, 2, 2, 2],
  },
  [BuildingType.LargeRunway]: {
    type: BuildingType.LargeRunway,
    key: 'largeRunway',
    width: 64,
    height: 6,
    levels: [lv(900_000, 24, 3)],
    limits: [0, 0, 0, 1, 2, 2],
  },
  // As wide as the large runway from the start (its strip: shoulders round a narrower runway
  // that widens with the levels), so growing never needs more room.
  [BuildingType.MainRunway]: {
    type: BuildingType.MainRunway,
    key: 'mainRunway',
    width: 64,
    height: 6,
    levels: [
      lv(40_000, 3, 1),
      lv(80_000, 6, 1),
      lv(140_000, 8, 2),
      lv(300_000, 16, 3),
      lv(600_000, 24, 3),
    ],
    limits: [0, 1, 1, 2, 4, 4],
  },
  [BuildingType.SmallStand]: {
    type: BuildingType.SmallStand,
    key: 'smallStand',
    width: 6,
    height: 6,
    levels: [lv(6_000, 1, 1)],
    limits: [0, 4, 6, 8, 10, 12],
  },
  [BuildingType.LargeStand]: {
    type: BuildingType.LargeStand,
    key: 'largeStand',
    width: 18,
    height: 18,
    levels: [lv(160_000, 8, 3)],
    limits: [0, 0, 0, 2, 4, 6],
  },
  [BuildingType.SmallParking]: {
    type: BuildingType.SmallParking,
    key: 'smallParking',
    width: 6,
    height: 6,
    levels: [lv(4_000, 1, 1)],
    limits: [0, 2, 4, 6, 8, 12],
  },
  [BuildingType.LargeParking]: {
    type: BuildingType.LargeParking,
    key: 'largeParking',
    width: 18,
    height: 18,
    levels: [lv(60_000, 4, 3)],
    limits: [0, 0, 0, 2, 4, 6],
  },
  [BuildingType.MilitaryTower]: {
    type: BuildingType.MilitaryTower,
    key: 'militaryTower',
    width: 6,
    height: 6,
    levels: [lv(120_000, 8, 3), lv(240_000, 12, 4), lv(480_000, 16, 5)],
    limits: [0, 0, 0, 1, 1, 1],
  },
  [BuildingType.Parking]: {
    type: BuildingType.Parking,
    key: 'parking',
    width: 8,
    height: 8,
    levels: [lv(10_000, 1, 1), lv(35_000, 4, 2), lv(90_000, 10, 4)],
    limits: [0, 1, 2, 2, 3, 4],
  },
  [BuildingType.FuelDepot]: {
    type: BuildingType.FuelDepot,
    key: 'fuelDepot',
    width: 6,
    height: 6,
    levels: [lv(25_000, 2, 2), lv(70_000, 6, 3), lv(160_000, 12, 4)],
    limits: [0, 0, 1, 1, 2, 2],
  },
  [BuildingType.FireStation]: {
    type: BuildingType.FireStation,
    key: 'fireStation',
    width: 6,
    height: 6,
    levels: [lv(20_000, 2, 1), lv(55_000, 6, 3), lv(130_000, 12, 5)],
    limits: [0, 1, 1, 1, 2, 2],
  },
  [BuildingType.Hangar]: {
    type: BuildingType.Hangar,
    key: 'hangar',
    width: 12,
    height: 10,
    levels: [lv(50_000, 4, 2), lv(140_000, 10, 3), lv(320_000, 20, 5)],
    limits: [0, 0, 1, 1, 2, 2],
  },
  [BuildingType.GroundHandling]: {
    type: BuildingType.GroundHandling,
    key: 'groundHandling',
    width: 10,
    height: 8,
    levels: [
      lv(30_000, 2, 1),
      lv(75_000, 6, 2),
      lv(170_000, 12, 3),
      lv(380_000, 18, 4),
      lv(620_000, 24, 5),
      lv(900_000, 30, 5),
    ],
    // Three from tower Lv4: a busy airport outgrows two bases' fleets (the 0.53 playtest).
    limits: [0, 1, 1, 1, 3, 3],
  },
  [BuildingType.GrantSign]: {
    type: BuildingType.GrantSign,
    key: 'grantSign',
    width: 8,
    height: 2,
    levels: [lv(4_000, 1, 0)],
    limits: [1, 1, 1, 1, 1, 1],
  },
  [BuildingType.TransitHub]: {
    type: BuildingType.TransitHub,
    key: 'transitHub',
    width: 10,
    height: 6,
    levels: [lv(35_000, 3, 1), lv(80_000, 8, 2)],
    limits: [0, 1, 1, 2, 2, 3],
  },
  [BuildingType.CargoTerminal]: {
    type: BuildingType.CargoTerminal,
    key: 'cargoTerminal',
    width: 14,
    height: 10,
    levels: [lv(70_000, 4, 2), lv(160_000, 8, 3), lv(340_000, 14, 4)],
    limits: [0, 0, 1, 1, 2, 2],
  },
  [BuildingType.WildlifeControl]: {
    type: BuildingType.WildlifeControl,
    key: 'wildlifeControl',
    width: 6,
    height: 4,
    levels: [lv(30_000, 2, 2)],
    limits: [0, 0, 1, 1, 1, 1],
  },
  // The military zone (tower level 3 on): a gate first, then where the aircraft park and what
  // a squadron needs.
  [BuildingType.MilitaryGate]: {
    type: BuildingType.MilitaryGate,
    key: 'militaryGate',
    width: 6,
    height: 4,
    levels: [lv(60_000, 6, 3), lv(90_000, 10, 4)],
    limits: [0, 0, 0, 1, 1, 1],
  },
  // Narrower than a stand, so two fit along a terminal's airside face and one at each end: up to
  // a narrow-body (a wide-body needs a level 2 stand).
  [BuildingType.ContactStand]: {
    type: BuildingType.ContactStand,
    key: 'contactStand',
    width: 8,
    height: 10,
    levels: [lv(55_000, 4, 2)],
    limits: [0, 0, 2, 4, 8, 12],
  },
  [BuildingType.RemoteParking]: {
    type: BuildingType.RemoteParking,
    key: 'remoteParking',
    width: 10,
    height: 10,
    levels: [lv(12_000, 1, 2)],
    limits: [0, 0, 3, 5, 8, 12],
  },
  [BuildingType.Helipad]: {
    type: BuildingType.Helipad,
    key: 'helipad',
    width: 8,
    height: 8,
    levels: [lv(60_000, 6, 1)],
    limits: [0, 1, 2, 2, 3, 4],
  },
  [BuildingType.SpotterDeck]: {
    type: BuildingType.SpotterDeck,
    key: 'spotterDeck',
    width: 6,
    height: 6,
    levels: [lv(25_000, 3, 1), lv(45_000, 5, 2)],
    limits: [0, 1, 1, 2, 2, 3],
  },
  [BuildingType.DeicePad]: {
    type: BuildingType.DeicePad,
    key: 'deicePad',
    width: 10,
    height: 10,
    levels: [lv(110_000, 8, 3)],
    limits: [0, 0, 0, 1, 2, 3],
  },
  [BuildingType.LargeDeicePad]: {
    type: BuildingType.LargeDeicePad,
    key: 'largeDeicePad',
    width: 18,
    height: 18,
    levels: [lv(260_000, 12, 4)],
    limits: [0, 0, 0, 0, 2, 2],
  },
  [BuildingType.Directorate]: {
    type: BuildingType.Directorate,
    key: 'directorate',
    width: 10,
    height: 8,
    levels: [lv(140_000, 10, 3)],
    limits: [0, 0, 0, 1, 1, 1],
  },
  [BuildingType.Cafe]: {
    type: BuildingType.Cafe,
    key: 'cafe',
    width: 4,
    height: 4,
    levels: [lv(25_000, 2, 1), lv(55_000, 4, 2)],
    limits: [0, 4, 6, 8, 10, 12],
  },
  [BuildingType.Restaurant]: {
    type: BuildingType.Restaurant,
    key: 'restaurant',
    width: 6,
    height: 6,
    levels: [lv(70_000, 5, 2), lv(140_000, 8, 3)],
    limits: [0, 0, 3, 5, 7, 9],
  },
  [BuildingType.Mall]: {
    type: BuildingType.Mall,
    key: 'mall',
    width: 16,
    height: 12,
    levels: [lv(450_000, 20, 3), lv(800_000, 28, 4), lv(1_250_000, 36, 5)],
    limits: [0, 0, 0, 1, 2, 2],
  },
  [BuildingType.FuelStation]: {
    type: BuildingType.FuelStation,
    key: 'fuelStation',
    width: 8,
    height: 6,
    levels: [lv(80_000, 4, 1), lv(150_000, 6, 3)],
    limits: [0, 1, 2, 3, 4, 4],
  },
  [BuildingType.OfficePark]: {
    type: BuildingType.OfficePark,
    key: 'officePark',
    width: 8,
    height: 8,
    levels: [lv(300_000, 14, 3), lv(550_000, 20, 4), lv(850_000, 26, 5)],
    limits: [0, 0, 0, 2, 4, 6],
  },
  [BuildingType.CityPark]: {
    type: BuildingType.CityPark,
    key: 'cityPark',
    width: 6,
    height: 6,
    levels: [lv(15_000, 2, 1), lv(35_000, 3, 2)],
    limits: [0, 3, 5, 8, 10, 12],
  },
  [BuildingType.Shelter]: {
    type: BuildingType.Shelter,
    key: 'shelter',
    width: 10,
    height: 10,
    levels: [lv(140_000, 18, 4)],
    limits: [0, 0, 0, 0, 4, 6],
  },
  [BuildingType.MilitaryApron]: {
    type: BuildingType.MilitaryApron,
    key: 'militaryApron',
    width: 10,
    height: 10,
    levels: [lv(80_000, 10, 4)],
    limits: [0, 0, 0, 0, 3, 5],
  },
  [BuildingType.OpsCenter]: {
    type: BuildingType.OpsCenter,
    key: 'opsCenter',
    width: 8,
    height: 6,
    levels: [lv(90_000, 12, 4)],
    limits: [0, 0, 0, 0, 1, 1],
  },
  [BuildingType.Munitions]: {
    type: BuildingType.Munitions,
    key: 'munitions',
    width: 6,
    height: 6,
    levels: [lv(70_000, 8, 4)],
    limits: [0, 0, 0, 0, 1, 1],
  },
  [BuildingType.AirDefence]: {
    type: BuildingType.AirDefence,
    key: 'airDefence',
    width: 10,
    height: 8,
    levels: [lv(120_000, 12, 4), lv(180_000, 16, 5)],
    limits: [0, 0, 0, 0, 1, 1],
  },
  [BuildingType.CateringKitchen]: {
    type: BuildingType.CateringKitchen,
    key: 'cateringKitchen',
    width: 10,
    height: 8,
    levels: [lv(90_000, 8, 3), lv(200_000, 14, 4)],
    limits: [0, 0, 0, 1, 1, 1],
  },
  [BuildingType.ConstructionOffice]: {
    type: BuildingType.ConstructionOffice,
    key: 'constructionOffice',
    width: 8,
    height: 6,
    levels: [lv(45_000, 4, 2), lv(110_000, 8, 3), lv(220_000, 12, 4)],
    limits: [0, 0, 1, 1, 1, 1],
  },
  [BuildingType.RailStation]: {
    type: BuildingType.RailStation,
    key: 'railStation',
    width: 16,
    height: 8,
    levels: [lv(280_000, 18, 4), lv(520_000, 24, 5)],
    limits: [0, 0, 0, 0, 1, 1],
  },
  [BuildingType.Hotel]: {
    type: BuildingType.Hotel,
    key: 'hotel',
    width: 10,
    height: 8,
    levels: [lv(150_000, 10, 3), lv(320_000, 16, 4), lv(600_000, 24, 5)],
    limits: [0, 0, 0, 1, 1, 2],
  },
};

/**
 * Kinds no longer offered for building: the three runways MainRunway replaced. Those already
 * built go on working (and can be moved or upgraded as before).
 */
export const RETIRED_TYPES: ReadonlySet<number> = new Set<number>([
  BuildingType.Runway,
  BuildingType.SmallRunway,
  BuildingType.LargeRunway,
]);

/** Catalogue order for menus. */
export const BUILDING_ORDER: readonly BuildingTypeId[] = [
  BuildingType.Tower,
  BuildingType.Terminal,
  BuildingType.MainRunway,
  BuildingType.Runway,
  BuildingType.Stand,
  BuildingType.SmallRunway,
  BuildingType.LargeRunway,
  BuildingType.SmallStand,
  BuildingType.LargeStand,
  BuildingType.ContactStand,
  BuildingType.DeicePad,
  BuildingType.LargeDeicePad,
  BuildingType.RemoteParking,
  BuildingType.SmallParking,
  BuildingType.LargeParking,
  BuildingType.Parking,
  BuildingType.TransitHub,
  BuildingType.FireStation,
  BuildingType.FuelDepot,
  BuildingType.GroundHandling,
  BuildingType.Hangar,
  BuildingType.CargoTerminal,
  BuildingType.Hotel,
  BuildingType.RailStation,
  BuildingType.ConstructionOffice,
  BuildingType.Directorate,
  BuildingType.CateringKitchen,
  BuildingType.WildlifeControl,
  BuildingType.MilitaryGate,
  BuildingType.MilitaryTower,
  BuildingType.Shelter,
  BuildingType.MilitaryApron,
  BuildingType.OpsCenter,
  BuildingType.Munitions,
  BuildingType.AirDefence,
  BuildingType.Helipad,
  BuildingType.SpotterDeck,
  BuildingType.Cafe,
  BuildingType.Restaurant,
  BuildingType.Mall,
  BuildingType.FuelStation,
  BuildingType.OfficePark,
  BuildingType.CityPark,
  BuildingType.GrantSign,
];

export function buildingDef(type: number): BuildingDef | undefined {
  return BUILDINGS[type as BuildingTypeId];
}

/** A placed building. `level` counts completed levels: 0 while the first construction runs. */
export interface Building {
  readonly id: number;
  readonly type: BuildingTypeId;
  /** Top-left tile of the (rotated) footprint (the player may move it, see moveBuilding). */
  x: number;
  y: number;
  /** Quarter turns, 0–3. */
  rot: number;
  level: number;
  /** Ticks left in the running construction or upgrade; 0 when idle. */
  buildRemaining: number;
  /** Total ticks of the running construction or upgrade. */
  buildTotal: number;
  /** Cents spent on this building so far (for refunds). */
  spent: number;
  /** Runways: how the tower uses it (RunwayMode). 0 for other buildings. */
  mode: number;
  /** Runways: direction in use, 0 or 1 (see runwayHeading). Follows the wind. */
  flow: number;
  /** Terminals: facilities bought (packed counts, see facilities.ts). 0 for other buildings. */
  facilities: number;
  /** Runways: surface wear in per mille (see pavement.ts). 0 for other buildings. */
  wear: number;
  /** Runways: who may use it (RunwayUsage). 0 for other buildings. */
  usage: number;
  /** Large runways and stands: 1 when kept for the big aircraft (see isBig). 0 otherwise. */
  bigOnly: number;
}

/** How a runway is used. Wire and save ids: append only. */
export const RunwayMode = { Mixed: 0, Arrivals: 1, Departures: 2 } as const;
export type RunwayModeId = (typeof RunwayMode)[keyof typeof RunwayMode];

/**
 * Who a runway serves: everyone (joint civil and military use, the default), civil traffic only
 * or military only. A scramble closes every runway military aircraft may use. Wire and save
 * ids: append only.
 */
export const RunwayUsage = { Joint: 0, Civil: 1, Military: 2 } as const;
export type RunwayUsageId = (typeof RunwayUsage)[keyof typeof RunwayUsage];

/** The subset of Building that read-only consumers (UI, render) need. */
export type BuildingView = Pick<
  Building,
  | 'id'
  | 'type'
  | 'x'
  | 'y'
  | 'rot'
  | 'level'
  | 'buildRemaining'
  | 'buildTotal'
  | 'mode'
  | 'flow'
  | 'facilities'
  | 'wear'
  | 'usage'
  | 'bigOnly'
>;

/**
 * Sizes of runways, stands and parking positions, and of the aircraft they take: a small one
 * takes small aircraft only, a medium one (the original kinds) small and medium, a large one any
 * (unless kept for the big ones, see Building.bigOnly).
 */
export const Size = { Small: 0, Medium: 1, Large: 2 } as const;
export type SizeId = (typeof Size)[keyof typeof Size];

/** Every kind of runway. */
export const RUNWAY_TYPES: ReadonlySet<number> = new Set<number>([
  BuildingType.Runway,
  BuildingType.SmallRunway,
  BuildingType.LargeRunway,
  BuildingType.MainRunway,
]);

/** Whether a building type is a runway (of any size). */
export function isRunway(type: number): boolean {
  return RUNWAY_TYPES.has(type);
}

/** The size of a runway, stand or parking position type (medium for anything else). */
export function sizeOf(type: number): SizeId {
  switch (type) {
    case BuildingType.SmallRunway:
    case BuildingType.SmallStand:
    case BuildingType.SmallParking:
      return Size.Small;
    case BuildingType.LargeRunway:
    case BuildingType.LargeStand:
    case BuildingType.LargeParking:
    case BuildingType.LargeDeicePad:
      return Size.Large;
    default:
      return Size.Medium;
  }
}

/**
 * The runway (MainRunway) level by level: the aircraft it takes (as the original runway's levels
 * go: 1 the small ones, 2 narrowbodies, 3 widebodies), what it is equipped with (1 nothing, 2
 * lights and a CAT I ILS, 3 approach lights and a CAT III ILS) and its size (large at the top).
 */
const MAIN_RUNWAY_GRADE = [0, 1, 2, 2, 3, 3] as const;
const MAIN_RUNWAY_LIGHTS = [0, 1, 1, 2, 3, 3] as const;
/** The level from which the runway is a large one (it takes jumbos and giant freighters). */
export const MAIN_RUNWAY_LARGE = 5;

/**
 * Which aircraft a runway takes, as the original runway's levels go (an aircraft class's
 * runwayLevel): a small strip 1, a large runway 3.
 */
export function runwayGrade(b: Pick<Building, 'type' | 'level'>): number {
  if (b.type === BuildingType.SmallRunway) return Math.min(1, b.level);
  if (b.type === BuildingType.LargeRunway) return b.level >= 1 ? 3 : 0;
  if (b.type === BuildingType.MainRunway) return MAIN_RUNWAY_GRADE[b.level] ?? 3;
  return b.level;
}

/**
 * What a runway is equipped with, as the original runway's levels go: 1 nothing (day flying in
 * good visibility), 2 lights and a CAT I ILS, 3 approach lights and a CAT III ILS. The same as
 * its grade for the runways before MainRunway.
 */
export function runwayLights(b: Pick<Building, 'type' | 'level'>): number {
  if (b.type === BuildingType.MainRunway) return MAIN_RUNWAY_LIGHTS[b.level] ?? 3;
  return runwayGrade(b);
}

/**
 * The runway's (MainRunway's) level an aircraft class needs, for the player to read: the first
 * whose grade reaches the class's runwayLevel (narrowbodies 2, widebodies 4), the large runway's
 * for the big ones.
 */
export function mainRunwayLevelFor(cls: {
  readonly runwayLevel: number;
  readonly size: SizeId;
}): number {
  if (cls.size === Size.Large) return MAIN_RUNWAY_LARGE;
  const level = MAIN_RUNWAY_GRADE.findIndex((grade) => grade >= cls.runwayLevel);
  return level < 0 ? MAIN_RUNWAY_LARGE : level;
}

/** A runway's size (see Size): MainRunway's by its level, the others' by their kind. */
export function runwaySize(b: Pick<Building, 'type' | 'level'>): SizeId {
  if (b.type === BuildingType.MainRunway) {
    return b.level >= MAIN_RUNWAY_LARGE ? Size.Large : Size.Medium;
  }
  return sizeOf(b.type);
}

/** Whether a runway, stand or parking position can be kept for the big aircraft (Building.bigOnly). */
export function canBeBigOnly(b: Pick<Building, 'type' | 'level'>): boolean {
  if (b.type === BuildingType.MainRunway) return b.level >= MAIN_RUNWAY_LARGE;
  return (
    b.type === BuildingType.LargeRunway ||
    b.type === BuildingType.LargeStand ||
    b.type === BuildingType.LargeParking
  );
}

/** Footprint size after rotation. */
export function footprint(def: BuildingDef, rot: number): { w: number; h: number } {
  return rot % 2 === 0 ? { w: def.width, h: def.height } : { w: def.height, h: def.width };
}

/**
 * Which way a building's back faces, by rotation (models put the front on local +Z): a
 * terminal's landside, the side of a contact stand that is set against its terminal.
 */
export function backDirection(rot: number): readonly [number, number] {
  return BACK[rot & 3] as readonly [number, number];
}
const BACK: readonly (readonly [number, number])[] = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];

/**
 * The terminal a contact stand at (x, y, rot) is set against: its back all along one face of the
 * terminal's footprint, any face but the landside one (where the road is). Its building id, or 0.
 * `occupantAt` gives the building on a tile (0 for none).
 */
export function contactTerminal(
  buildings: readonly Pick<Building, 'id' | 'type' | 'rot'>[],
  occupantAt: (x: number, y: number) => number,
  x: number,
  y: number,
  rot: number,
): number {
  const def = BUILDINGS[BuildingType.ContactStand];
  const { w, h } = footprint(def, rot);
  const [dx, dy] = backDirection(rot);
  // The row of tiles just behind the stand.
  const tiles: [number, number][] = [];
  if (dy !== 0) {
    const ty = dy < 0 ? y - 1 : y + h;
    for (let tx = x; tx < x + w; tx++) tiles.push([tx, ty]);
  } else {
    const tx = dx < 0 ? x - 1 : x + w;
    for (let ty = y; ty < y + h; ty++) tiles.push([tx, ty]);
  }
  const first = tiles[0] as [number, number];
  const id = occupantAt(first[0], first[1]);
  if (id === 0 || tiles.some(([tx, ty]) => occupantAt(tx, ty) !== id)) return 0;
  const terminal = buildings.find((b) => b.id === id);
  if (!terminal || terminal.type !== BuildingType.Terminal) return 0;
  // Not on its landside: that face is the kerb's.
  const [lx, ly] = backDirection(terminal.rot);
  if (lx === -dx && ly === -dy) return 0;
  return id;
}

/** Completed level of the control tower, 0 if there is none yet. */
export function towerLevel(buildings: readonly BuildingView[]): number {
  for (const b of buildings) if (b.type === BuildingType.Tower) return b.level;
  return 0;
}

export function countOfType(buildings: readonly BuildingView[], type: number): number {
  let n = 0;
  for (const b of buildings) if (b.type === type) n++;
  return n;
}

export function crewsBusy(buildings: readonly BuildingView[]): number {
  let n = 0;
  for (const b of buildings) if (b.buildRemaining > 0) n++;
  return n;
}

/**
 * Building crews the airport has: the standing ones plus one per level of a finished
 * construction office (its own upgrade keeps the crews it already has).
 */
export function crewsTotal(buildings: readonly BuildingView[]): number {
  let office = 0;
  for (const b of buildings) {
    if (b.type === BuildingType.ConstructionOffice) office = Math.max(office, b.level);
  }
  return CONSTRUCTION_CREWS + office;
}

export function crewsFree(buildings: readonly BuildingView[]): number {
  return crewsTotal(buildings) - crewsBusy(buildings);
}

export function buildingAt(
  buildings: readonly BuildingView[],
  x: number,
  y: number,
): BuildingView | undefined {
  for (const b of buildings) {
    const def = buildingDef(b.type);
    if (!def) continue;
    const { w, h } = footprint(def, b.rot);
    if (x >= b.x && y >= b.y && x < b.x + w && y < b.y + h) return b;
  }
  return undefined;
}
